import { useFormStore } from "@/stores/use-form-store"
import { getMonth, getYear } from "date-fns"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import FieldInfoIcon from "../../components/field-info"
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../../components/ui/form"
import { cn, parseAttributes, toDateSafe } from "../../lib/utils"
import {
	DateInputConfig,
	type Attribute,
	type FieldProps,
} from "../../types/form-types"
import { Description } from "./components/description"
import { logger } from "@/utils/logger"
import { useLogicStore } from "@/stores/use-conditional-logic-store"
import { ErrorMessage } from "./components/error-message"

const range = (start: number, end: number, step = 1) =>
	Array.from(
		{ length: Math.ceil((end - start) / step) },
		(_, i) => start + i * step,
	)

export function DateField({ form, field, commonAttributes }: FieldProps) {
	const {
		label,
		infoIconText,
		infoIconType,
		description,
		infoIconRequired,
		readOnly,
	} = commonAttributes
	const { shouldDisable, verifiedFields } = useFormStore()

	const isVerified = verifiedFields.get(field.field_id) || false

	// Parse attributes
	const { minDate, maxDate } = getDateInputAttributes(field.attributes)
	const displayFormat = "MM/DD/YYYY"

	const years = range(1950, getYear(new Date()) + 1, 1)
	const months = [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December",
	]
	const { hiddenFields, errors } = useLogicStore()

	if (hiddenFields[field.field_id]) return null

	return (
		<FormField
			name={field.field_id}
			control={form.control}
			render={({ field: formField }) => {
				// Ensure the value is a Date object (null if empty)
				const selectedDate = toDateSafe(formField.value)
				logger.debug("selectedDate", selectedDate)
				return (
					<FormItem className="flex flex-col">
						<FormLabel>
							<div className="flex flex-col gap-1">
								<div className="flex items-center gap-1">
									<p
										className={cn(
											field.is_required &&
												"after:ml-1 after:text-red-500 after:content-['*']",
										)}
									>
										{label}
									</p>
									{infoIconRequired && (
										<FieldInfoIcon
											infoText={infoIconText}
											iconType={infoIconType}
										/>
									)}
								</div>
								{description && (
									<Description description={description} />
								)}
							</div>
						</FormLabel>

						<FormControl>
							<DatePicker
								id={field.field_id}
								selected={selectedDate}
								onChange={(date: Date | null) => {
									formField.onChange(date) // ⬅️ store Date object directly
								}}
								// dont use this date format, it breaks the formatting
								// dateFormat={displayFormat} // only affects how it shows in input
								placeholderText={displayFormat}
								minDate={minDate}
								maxDate={maxDate}
								disabled={
									isVerified || readOnly || shouldDisable
								}
								className={cn(
									"w-full rounded-md border px-3 py-2",
								)}
								showMonthDropdown
								showYearDropdown
								scrollableYearDropdown
								required={field.is_required}
								renderCustomHeader={({
									date,
									changeYear,
									changeMonth,
									decreaseMonth,
									increaseMonth,
									prevMonthButtonDisabled,
									nextMonthButtonDisabled,
								}) => (
									<div className="flex items-center justify-between rounded-t-lg px-4 py-2">
										{/* Previous Button */}
										<button
											onClick={decreaseMonth}
											disabled={prevMonthButtonDisabled}
											type="button"
											className="rounded-full px-2 py-1 transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
										>
											◀
										</button>

										{/* Month & Year Dropdowns */}
										<div className="flex items-center gap-2">
											<select
												value={months[getMonth(date)]}
												onChange={({
													target: { value },
												}) =>
													changeMonth(
														months.indexOf(value),
													)
												}
												className="rounded-md border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
											>
												{months.map(option => (
													<option
														key={option}
														value={option}
													>
														{option}
													</option>
												))}
											</select>

											<select
												value={getYear(date)}
												onChange={({
													target: { value },
												}) => changeYear(+value)}
												className="rounded-md border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
											>
												{years.map(option => (
													<option
														key={option}
														value={option}
													>
														{option}
													</option>
												))}
											</select>
										</div>

										{/* Next Button */}
										<button
											type="button"
											onClick={increaseMonth}
											disabled={nextMonthButtonDisabled}
											className="rounded-full px-2 py-1 transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
										>
											▶
										</button>
									</div>
								)}
							/>
						</FormControl>

						{/* <Popover>
							<PopoverTrigger asChild>
								<FormControl>
									<Button
										variant={"outline"}
										className={cn(
											"w-[240px] pl-3 text-left font-normal",
											!formField.value &&
												"text-muted-foreground",
										)}
									>
										{formField.value ? (
											format(formField.value, "PPP")
										) : (
											<span>Pick a date</span>
										)}
										<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
									</Button>
								</FormControl>
							</PopoverTrigger>
							<PopoverContent
								className="w-auto p-0"
								align="start"
							>
								<Calendar
									mode="single"
									selected={formField.value}
									onSelect={formField.onChange}
									disabled={date =>
										date > maxDate! || date < minDate!
									}
									captionLayout="dropdown"
									// hidden={{
									// 	before: minDate!,
									// 	after: maxDate!,
									// }}
								/>
							</PopoverContent>
						</Popover> */}

						<FormMessage />
						<ErrorMessage error={errors[field.field_id]} />
					</FormItem>
				)
			}}
		/>
	)
}

function getDateInputAttributes(attributes: Attribute[]) {
	const props: {
		minDate?: Date
		maxDate?: Date
		dateFormat?: string
	} = {}

	const parsedAttributes = parseAttributes(attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case DateInputConfig.MinDate:
				props.minDate = new Date(value as string)
				break
			case DateInputConfig.MaxDate:
				props.maxDate = new Date(value as string)
				break
			case DateInputConfig.DateFormat:
				props.dateFormat = value as string
				break
		}
	})
	return props
}
